import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import GroceryList from './GroceryList.jsx'
import CategorySuggestionModal from './CategorySuggestionModal.jsx'
import UploadErrorModal from './UploadErrorModal.jsx'
import RecipeRenameModal from './RecipeRenameModal.jsx'
import StoreSelector from './components/StoreSelector.jsx'
import DismissibleOnboardingMessage from './components/DismissibleOnboardingMessage.jsx'
import useOnboardingVisibility from './hooks/useOnboardingVisibility.js'
import { useShoppingListStateMachine } from './hooks/useShoppingListStateMachine.js'
import { ONBOARDING_BANNER_FLAGS, ONBOARDING_MESSAGES } from './utils/onboardingRegistry.js'
import { authenticatedFetch, getAuthHeaders } from './utils/auth.js'
import { useAuth } from './contexts/AuthContext.jsx'
import { useSelectedStore } from './contexts/useSelectedStore.js'
import { useShopRefreshOrchestrator } from './hooks/useShopRefreshOrchestrator.js'
import { useChatListHydrationOrchestrator } from './hooks/useChatListHydrationOrchestrator.js'
import { capitalizeFirstGroceryWord } from './utils/groceryItemDisplayName.js'
import { loadInitialGroceryListFromStorage, mapGroceryItemsDisplayNames } from './utils/groceryListStorage.js'
import { isSavedListRecipe } from './utils/savedListRecipe.js'
import { invalidateSavedListsCache } from './savedListsCache.js'
import { LIST_ACTIONS } from './utils/shoppingListActions.js'
import { stampRecipeSourceOnItems } from './utils/shoppingListState.js'
import { appendRecipeIngredientsToCurrentShoppingList, CURRENT_SHOPPING_LIST_NAME, resolveCurrentShoppingListId } from './utils/appendRecipeIngredientsToShoppingList.js'
import { Button, TextArea } from './design-system/primitives/index.js'
import StateBanner from './components/StateBanner/StateBanner.jsx'
import StateBannerStack from './components/StateBanner/StateBannerStack.jsx'

/** Set to false to revert to the previous 3-tab layout (Lookup | Shop | Lists). */
const USE_UNIFIED_SHOPPING_VIEW = true
const CART_READ_ENABLED = String(import.meta.env.VITE_CART_READ_ENABLED || '').toLowerCase() === 'true'
const CART_DUAL_WRITE_ENABLED = String(import.meta.env.VITE_CART_DUAL_WRITE_ENABLED || '').toLowerCase() === 'true'

function pantryNamesFromGroupedInventory(data) {
  if (!data || typeof data !== 'object') return []
  const normalize = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ')
  const names = []
  const seen = new Set()
  for (const bucket of [data.proteins, data.fruits_vegetables, data.dairy, data.spices, data.staples]) {
    if (!Array.isArray(bucket)) continue
    bucket.forEach((it) => {
      const n = normalize(it.item_name)
      if (n && !seen.has(n)) {
        seen.add(n)
        names.push(n)
      }
    })
  }
  return names
}
const PLANNED_RECIPE_SHORTCUT_LIMIT = 3

function formatPlannedRecipeDate(value) {
  if (!value) return null
  const d = new Date(`${value}T00:00:00`)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' }).format(d)
}

function plannedItemsToRecipeShortcuts(items) {
  if (!Array.isArray(items)) return []
  return items
    .filter((item) => item?.recipe_id && item?.date && item?.status === 'planned')
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .slice(0, PLANNED_RECIPE_SHORTCUT_LIMIT)
    .map((item) => {
      const totalCount = Number(item?.total_count)
      const matchedCount = Number(item?.matched_count)
      const missingCount =
        Number.isFinite(totalCount) && Number.isFinite(matchedCount)
          ? Math.max(0, totalCount - matchedCount)
          : null
      return {
        id: String(item.recipe_id),
        planItemId: item.id ? String(item.id) : `${item.recipe_id}-${item.date}`,
        name: item.recipe_name || 'Planned recipe',
        item_count: Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 0,
        missing_count: missingCount,
        plannedDate: item.date,
        plannedDateLabel: formatPlannedRecipeDate(item.date),
      }
    })
}

/** Recipe ids that already contributed lines to the visible Shop list (Plan or Recipe Details). */
function collectRecipeIdsOnGroceryList(items) {
  const ids = new Set()
  if (!Array.isArray(items)) return ids
  for (const item of items) {
    const single = item?.source_recipe_id != null ? String(item.source_recipe_id).trim() : ''
    if (single) ids.add(single)
    const multi = Array.isArray(item?.source_recipe_ids) ? item.source_recipe_ids : []
    for (const raw of multi) {
      const id = raw != null ? String(raw).trim() : ''
      if (id) ids.add(id)
    }
  }
  return ids
}

function partitionLookupResults(rawResults) {
  const results = Array.isArray(rawResults) ? rawResults : []
  const successfulMatches = results.filter(
    (result) =>
      result.aisle_id !== 'Unknown' &&
      result.aisle_id !== 'Error' &&
      (result.confidence ?? 0) >= 0.6
  )
  const failedMatches = results.filter(
    (result) =>
      result.aisle_id === 'Unknown' ||
      result.aisle_id === 'Error' ||
      (result.confidence ?? 0) < 0.6
  )
  const groupedByAisle = successfulMatches.reduce((acc, result) => {
    const aisleKey = `${result.aisle_id}-${result.aisle_name}`
    if (!acc[aisleKey]) {
      acc[aisleKey] = {
        aisle_id: result.aisle_id,
        aisle_name: result.aisle_name,
        items: [],
      }
    }
    acc[aisleKey].items.push({ ...result, item: result.item || result.name })
    return acc
  }, {})
  const sortedAisles = Object.values(groupedByAisle).sort((a, b) => {
    const aNum = parseInt(String(a.aisle_id || '').replace(/\D/g, ''), 10) || 999
    const bNum = parseInt(String(b.aisle_id || '').replace(/\D/g, ''), 10) || 999
    return aNum - bNum
  })
  return { sortedAisles, failedMatches }
}

function buildGroceryItemsFromLookup({ sortedAisles = [], failedMatches = [], parsedNames = [], timestamp = Date.now() }) {
  const allItems = []
  sortedAisles.forEach((aisle) => {
    aisle.items.forEach((item) => {
      allItems.push({
        id: `${item.item}-${item.aisle_id}-${timestamp}-${Math.random()}`,
        item: capitalizeFirstGroceryWord(item.item),
        category: item.category,
        aisle_id: item.aisle_id,
        aisle_name: item.aisle_name,
        confidence: item.confidence,
        completed: false,
        completedAt: null,
        addedAt: timestamp,
      })
    })
  })
  failedMatches.forEach((item) => {
    const label = item.item || item.name
    if (!label) return
    allItems.push({
      id: `${label}-${timestamp}-${Math.random()}`,
      item: capitalizeFirstGroceryWord(label),
      category: item.category || null,
      aisle_id: null,
      aisle_name: null,
      confidence: item.confidence ?? null,
      completed: false,
      completedAt: null,
      addedAt: timestamp,
    })
  })
  if (allItems.length === 0) {
    parsedNames.forEach((name) => {
      const label = String(name || '').trim()
      if (!label) return
      allItems.push({
        id: `${label}-${timestamp}-${Math.random()}`,
        item: capitalizeFirstGroceryWord(label),
        category: null,
        aisle_id: null,
        aisle_name: null,
        confidence: null,
        completed: false,
        completedAt: null,
        addedAt: timestamp,
      })
    })
  }
  return allItems
}

function GrocerySearch({
  setShowLoginModal,
  initialLoadList,
  onClearPendingLoadList,
  pendingQueueLength = 0,
  pendingRecipeImageFile,
  onRecipeImageImportFlowComplete,
  guestMode = false,
}) {
  const { isAuthenticated: accountAuthenticated } = useAuth()
  const isAuthenticated = accountAuthenticated || guestMode
  const { selectedStore } = useSelectedStore()
  const { isVisible: isShopIntroVisible, dismiss: dismissShopIntro } = useOnboardingVisibility(ONBOARDING_MESSAGES.shopIntro.storageKey)
  const showShopIntro = ONBOARDING_BANNER_FLAGS.shopIntro && !isAuthenticated && isShopIntroVisible
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState([])
  const [error, setError] = useState('')
  const [noMatches, setNoMatches] = useState([])
  const [groceryListItems, setGroceryListItems] = useState(() => loadInitialGroceryListFromStorage())
  const [activeGroceryTab, setActiveGroceryTab] = useState(USE_UNIFIED_SHOPPING_VIEW ? 'shopping' : 'lookup') // unified: 'shopping'; legacy: 'lookup' | 'grocery-list'
  const [lookupResultsExpanded, setLookupResultsExpanded] = useState(true) // expandable "Found items" in unified view
  const [loadedSavedListId, setLoadedSavedListId] = useState(null) // Track which saved list is currently loaded
  const [loadedSavedListName, setLoadedSavedListName] = useState(null) // Track loaded list name
  const [loadedSavedListIsRecipe, setLoadedSavedListIsRecipe] = useState(false) // True when loaded list is a recipe (no updating ingredients)
  const [loadedSavedListOriginalItemNames, setLoadedSavedListOriginalItemNames] = useState(null) // Normalized item names of the loaded recipe (for comparison)
  // Normalized pantry item names (for "In pantry" tags on shop list). Set when authenticated.
  const [pantryItemNames, setPantryItemNames] = useState(null)

  const listSectionRef = useRef(null) // scroll target when unified view "go to list"
  const chatListHydratedIdRef = useRef(null)
  /** When true, skip chat-list auto-hydrate (avoids re-fetching items before server empty PUT completes). */
  const chatHydrationSuppressedRef = useRef(false)
  /** After a failed cart shadow read, skip re-fetch until this timestamp (avoids ERR_INSUFFICIENT_RESOURCES loops). */
  const cartShadowHydrateBackoffUntilRef = useRef(0)
  const prevGroceryListLenForShadowHydrateRef = useRef(0)
  const processedPendingRecipeImageFileRef = useRef(null)

  // Recipe parsing state
  const [inputType, setInputType] = useState(null) // 'recipe' or 'items'
  const [parsedItems, setParsedItems] = useState([])
  const [detectionConfidence, setDetectionConfidence] = useState(null)
  const [_parsingMetadata, setParsingMetadata] = useState(null)
  // Image upload parsing
  const [_recipeImageFile, _setRecipeImageFile] = useState(null)
  const [_uploadingImage, setUploadingImage] = useState(false)
  const [imageParsedItems, setImageParsedItems] = useState([])
  const [_imageOcrPreview, setImageOcrPreview] = useState("")
  const [recipeName, setRecipeName] = useState('')
  const [recipeAuthor, setRecipeAuthor] = useState('')
  const [savingRecipe, setSavingRecipe] = useState(false)
  const [recipeSaved, setRecipeSaved] = useState(false)
  const [recipeImportNotice, setRecipeImportNotice] = useState(null) // { name, addedCount } after image import
  const [addingFromSavedList, setAddingFromSavedList] = useState(false) // true while loading a saved list (e.g. from chat)
  const [recipeShortcuts, setRecipeShortcuts] = useState([])
  const [recipeShortcutsLoading, setRecipeShortcutsLoading] = useState(false)
  const [cartAdapterMismatch, setCartAdapterMismatch] = useState(null)

  // Expose the active shopping-list context to chat requests.
  // Recipe-origin lists are flagged so backend avoids mutating recipe ingredients.
  useEffect(() => {
    try {
      if (loadedSavedListId) {
        localStorage.setItem('active_saved_list_id', String(loadedSavedListId))
      } else {
        localStorage.removeItem('active_saved_list_id')
      }
      localStorage.setItem('active_saved_list_is_recipe', loadedSavedListIsRecipe ? 'true' : 'false')
    } catch {
      // Ignore localStorage errors.
    }
  }, [loadedSavedListId, loadedSavedListIsRecipe])

  // Helper to upload and parse selected recipe image (reserved for future image-upload UI)
  async function _uploadAndParseRecipeImage(file) {
    if (!file) return
    if (!file.size || file.size <= 0) {
      setError('Selected file appears empty. Please choose another image and try again.')
      return
    }
    setUploadingImage(true)
    setError('')
    let timeoutId = null
    try {
      const controller = new AbortController()
      timeoutId = setTimeout(() => controller.abort(), 90000) // 90s timeout (increased for classification fallbacks)
      const form = new FormData()
      form.append('file', file)
      const params = new URLSearchParams()
      if (selectedStore?.id) params.set('store', selectedStore.id)
      let res
      let responseText
      try {
        res = await authenticatedFetch(`/recipes/upload_image${params.toString() ? `?${params}` : ''}`, { method: 'POST', headers: getAuthHeaders(), body: form, signal: controller.signal })
        
        // Handle authentication errors
        if (res.status === 401 || res.status === 403) {
          console.warn('[GrocerySearch] Authentication error during image upload')
          const error = new Error('Authentication failed. Please refresh the page or log in again.')
          error.status = res.status
          error.httpStatus = res.status
          throw error
        }
        
        responseText = await res.text()
        if (!res.ok) {
          let msg = `HTTP ${res.status}`
          try {
            const err = JSON.parse(responseText)
            msg = err.detail || msg
          } catch {
            if (responseText) msg = responseText
          }
          console.log('Upload error response:', { status: res.status, message: msg, responseText }) // Debug
          const error = new Error(msg)
          error.status = res.status
          error.httpStatus = res.status  // Also set httpStatus for easier access
          throw error
        }
      } catch (fetchError) {
        if (fetchError.status) {
          fetchError.response = { status: fetchError.status }
        }
        throw fetchError
      }
      let data = {}
      try {
        data = JSON.parse(responseText)
      } catch {
        throw new Error('Invalid server response while uploading image')
      }
      setImageParsedItems(Array.isArray(data.items) ? data.items : [])
      setImageOcrPreview(data.ocr_text || '')
      // Recipe name: prefer extracted name from image, fallback to filename, then default
      let defaultName = 'Recipe'
      if (data.recipe_name && data.recipe_name.trim()) {
        // Use extracted recipe name from image
        defaultName = data.recipe_name.trim()
      } else {
        // Fallback to filename
        try {
          const fname = (data.filename || '').split('/').pop() || 'Recipe'
          defaultName = fname.replace(/\.[^/.]+$/, '')
        } catch {
          // Ignore errors when extracting filename
        }
      }
      setRecipeName(defaultName)
      
      // Only show rename modal if we have parsed items or results
      const hasItems = Array.isArray(data.items) && data.items.length > 0
      const hasResults = Array.isArray(data.results) && data.results.length > 0
      
      if (hasItems || hasResults) {
        // Store upload data for processing after rename
        setPendingUploadData(data)
        // Show rename modal first, before showing results
        setShowRenameModal(true)
      } else {
        // Avoid "nothing happened" UX when upload succeeds but parsing yields nothing.
        // Common causes: low-quality photo, recipe text not captured, or parser couldn't find ingredients.
        const extractedName = (data.recipe_name && String(data.recipe_name).trim()) ? String(data.recipe_name).trim() : null
        const baseMsg = "Upload succeeded, but we couldn't extract any ingredients from that image."
        const hint = "Try a clearer, flatter photo with good lighting, or a screenshot where the ingredients list is fully visible."
        setUploadErrorMessage(extractedName ? `${baseMsg} (Detected: "${extractedName}") ${hint}` : `${baseMsg} ${hint}`)
        setShowUploadErrorModal(true)
      }
    } catch (e) {
      console.log('Upload catch block:', e, 'Status:', e.status) // Debug
      if (e.name === 'AbortError') {
        setError('Upload timed out. Please check your connection and try again.')
      } else {
        const errorMessage = e.message || 'Unknown error'
        console.log('Error message:', errorMessage) // Debug
        
        // Check if this is an extraction failure error - be very liberal with matching
        const errorLower = errorMessage.toLowerCase()
        const statusCode = e.status || e.httpStatus || e.response?.status
        console.log('Checking extraction failure - Status:', statusCode, 'Message:', errorMessage) // Debug
        
        // 422 status code always indicates extraction failure
        const isExtractionFailure = statusCode === 422 || 
          errorLower.includes("couldn't read") ||
          errorLower.includes("couldn't read any text") || 
          errorLower.includes("doesn't appear to be a recipe") ||
          errorLower.includes("couldn't read any text from that image") ||
          errorLower.includes("text extracted from this image") ||
          errorLower.includes("using ocr or ai vision") ||
          errorLower.includes("extraction") ||
          errorLower.includes("we couldn't read") ||
          errorLower.includes("text doesn't appear") ||
          errorLower.includes("couldn't be extracted")
        
        console.log('Is extraction failure?', isExtractionFailure, 'Status:', statusCode, 'Error lower:', errorLower) // Debug
        
        if (isExtractionFailure) {
          // Show modal for extraction failures
          const modalMessage = errorLower.includes("couldn't read") || errorLower.includes("we couldn't read")
            ? "The upload failed because data couldn't be extracted from the image. Please try a clearer photo with better lighting and a flatter angle."
            : "The upload failed because data couldn't be extracted from the image. The image doesn't appear to contain readable recipe text."
          
          setUploadErrorMessage(modalMessage)
          setShowUploadErrorModal(true)
        } else {
          setError(`Image upload failed: ${errorMessage}`)
        }
      }
    } finally {
      try { 
        if (timeoutId) clearTimeout(timeoutId) 
      } catch {
        // Ignore errors when clearing timeout
      }
      setUploadingImage(false)
    }
  }

  // Category suggestion state
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [submittedSuggestions, setSubmittedSuggestions] = useState(new Set())
  // Upload error modal state
  const [showUploadErrorModal, setShowUploadErrorModal] = useState(false)
  const [uploadErrorMessage, setUploadErrorMessage] = useState('')
  // Recipe rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [pendingUploadData, setPendingUploadData] = useState(null) // Store upload results until rename is confirmed
  
  // Memoized handler for closing upload error modal to prevent unnecessary re-renders
  const handleCloseUploadErrorModal = useCallback(() => {
    setShowUploadErrorModal(false)
    onRecipeImageImportFlowComplete?.()
  }, [onRecipeImageImportFlowComplete])

  // When new search results arrive in unified view, expand the found-items section
  useEffect(() => {
    if (USE_UNIFIED_SHOPPING_VIEW && (results.length > 0 || noMatches.length > 0)) {
      setLookupResultsExpanded(true)
    }
  }, [results.length, noMatches.length])

  // Fetch pantry item names when authenticated (for "In pantry" tags on shop list).
  // Deferred so recipe-add path (initialLoadList / loadSavedList + aisle refresh at 1800ms) stays light.
  const PANTRY_FETCH_DELAY_MS = 2000
  useEffect(() => {
    if (!isAuthenticated) {
      setPantryItemNames(null)
      return
    }
    let cancelled = false
    const timeoutId = setTimeout(() => {
      authenticatedFetch('/inventory/grouped')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (cancelled || !data) return
          const names = pantryNamesFromGroupedInventory(data)
          setPantryItemNames(names.length ? names : null)
        })
        .catch(() => { if (!cancelled) setPantryItemNames(null) })
    }, PANTRY_FETCH_DELAY_MS)
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [isAuthenticated])

  const ensurePantryItemNames = useCallback(async () => {
    if (pantryItemNames?.length) return pantryItemNames
    try {
      const res = await authenticatedFetch('/inventory/grouped')
      if (!res.ok) return pantryItemNames
      const data = await res.json()
      const names = pantryNamesFromGroupedInventory(data)
      setPantryItemNames(names.length ? names : null)
      return names.length ? names : null
    } catch {
      return pantryItemNames
    }
  }, [pantryItemNames])

  const { dispatchListAction, dispatchListActionRef, loadedSavedListIdRef } = useShoppingListStateMachine({
    groceryListItems,
    loadedSavedListId,
    loadedSavedListName,
    loadedSavedListIsRecipe,
    loadedSavedListOriginalItemNames,
    setGroceryListItems,
    setLoadedSavedListId,
    setLoadedSavedListName,
    setLoadedSavedListIsRecipe,
    setLoadedSavedListOriginalItemNames,
    setActiveGroceryTab,
    useUnifiedShoppingView: USE_UNIFIED_SHOPPING_VIEW,
    mapDisplayItems: mapGroceryItemsDisplayNames,
  })

  const recipeIdsOnList = useMemo(
    () => collectRecipeIdsOnGroceryList(groceryListItems),
    [groceryListItems],
  )

  const pushCartMutation = useCallback(
    async ({ mode, items, sourceType, sourceId = null }) => {
      if (!accountAuthenticated || !CART_DUAL_WRITE_ENABLED) return
      try {
        const endpoint = '/cart/current/items'
        const method = mode === 'replace' ? 'PUT' : 'POST'
        await authenticatedFetch(endpoint, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            items,
            source_type: sourceType,
            source_id: sourceId,
          }),
        })
      } catch (err) {
        // Non-fatal in dual-write period.
        console.warn('[GrocerySearch] Cart dual-write mutation failed:', err)
      }
    },
    [accountAuthenticated]
  )

  // Listen for logout event and reset state.
  // Must be declared after useShoppingListStateMachine so refs are initialized.
  useEffect(() => {
    function handleLogout() {
      chatHydrationSuppressedRef.current = false
      chatListHydratedIdRef.current = null
      // Reset grocery list state
      dispatchListActionRef.current?.({ type: LIST_ACTIONS.CLEAR_ALL })
      setPantryItemNames(null)
      setActiveGroceryTab(USE_UNIFIED_SHOPPING_VIEW ? 'shopping' : 'lookup')
      // Clear any other relevant state
      setInput('')
      setResults([])
      setError('')
      setNoMatches([])
    }
    window.addEventListener('userLoggedOut', handleLogout)
    return () => window.removeEventListener('userLoggedOut', handleLogout)
  }, [dispatchListActionRef, loadedSavedListIdRef])

  const {
    shopRefreshError,
    clearShopRefreshError,
    retryShopRefresh,
    scheduleDelayedAisleRefresh,
  } = useShopRefreshOrchestrator({
    selectedStore,
    loadedSavedListId,
    groceryListItems,
    dispatchListAction,
    pendingLookupResults: results,
    setPendingLookupResults: setResults,
    setPendingLookupNoMatches: setNoMatches,
  })

  const { chatHydrationNotice } = useChatListHydrationOrchestrator({
    enabled: USE_UNIFIED_SHOPPING_VIEW,
    isAuthenticated,
    activeGroceryTab,
    groceryListItemCount: groceryListItems.length,
    loadedSavedListId,
    addingFromSavedList,
    selectedStoreId: selectedStore?.id || null,
    mapDisplayItems: mapGroceryItemsDisplayNames,
    chatHydrationSuppressedRef,
    chatListHydratedIdRef,
    dispatchListAction,
  })

  // When a saved list is deleted from Recipes/Cook, drop it from Shop if it was the loaded list.
  useEffect(() => {
    function onSavedListDeleted(e) {
      const id = e?.detail?.listId
      if (id == null) return
      if (String(loadedSavedListIdRef.current) !== String(id)) return
      chatListHydratedIdRef.current = null
      chatHydrationSuppressedRef.current = false
      dispatchListActionRef.current?.({ type: LIST_ACTIONS.CLEAR_ALL })
    }
    window.addEventListener('savedListDeleted', onSavedListDeleted)
    return () => window.removeEventListener('savedListDeleted', onSavedListDeleted)
  }, [dispatchListActionRef, loadedSavedListIdRef])

  const appendSavedListItemsById = useCallback(
    async (listId) => {
      if (!listId) return
      if (!isAuthenticated) {
        if (setShowLoginModal) setShowLoginModal(true)
        else window.dispatchEvent(new CustomEvent('showLoginModal'))
        return
      }
      setAddingFromSavedList(true)
      try {
        const params = new URLSearchParams()
        if (selectedStore?.id) params.set('store', selectedStore.id)
        const response = await fetch(`/saved-lists/${listId}${params.toString() ? `?${params}` : ''}`, {
          headers: getAuthHeaders(),
        })
        if (!response.ok) {
          console.error('Failed to load saved list:', response.status)
          return
        }
        const list = await response.json()
        if (list && list.items) {
          const recipe = isSavedListRecipe(list)
          if (recipe) {
            const recipeName = (list.name && String(list.name).trim()) || 'Recipe'
            const shoppingItems = stampRecipeSourceOnItems(list.items, {
              isRecipe: true,
              listId,
              listName: recipeName,
            })
            dispatchListAction({
              type: LIST_ACTIONS.APPEND_ITEMS,
              items: shoppingItems,
              listId: loadedSavedListIsRecipe ? null : loadedSavedListIdRef.current,
              listName: loadedSavedListIsRecipe ? null : loadedSavedListName,
              isRecipe: false,
            })
            void pushCartMutation({
              mode: 'append',
              items: shoppingItems,
              sourceType: 'recipe',
              sourceId: String(listId),
            })
          } else {
            // A non-recipe source is copied into the one live shopping list; it
            // does not become a second named Shop list.
            dispatchListAction({
              type: LIST_ACTIONS.APPEND_ITEMS,
              items: list.items,
              listId: loadedSavedListIsRecipe ? null : loadedSavedListIdRef.current,
              listName: loadedSavedListIsRecipe ? null : loadedSavedListName,
              isRecipe: false,
            })
            void pushCartMutation({
              mode: 'append',
              items: list.items,
              sourceType: 'saved_list',
              sourceId: String(listId),
            })
          }
          if (USE_UNIFIED_SHOPPING_VIEW && listSectionRef.current) {
            setTimeout(() => listSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
          }
          scheduleDelayedAisleRefresh(listId)
        }
      } catch (err) {
        console.error('Error loading saved list:', err)
      } finally {
        setAddingFromSavedList(false)
      }
    },
    [
      dispatchListAction,
      isAuthenticated,
      loadedSavedListIsRecipe,
      loadedSavedListName,
      loadedSavedListIdRef,
      pushCartMutation,
      selectedStore?.id,
      scheduleDelayedAisleRefresh,
      setShowLoginModal,
    ]
  )

  /** Shop "From planned recipes": add only ingredients not already in the pantry. */
  const appendMissingPlanRecipeItemsById = useCallback(
    async (listId, listName) => {
      if (!listId) return
      // Already contributed via Plan / Recipe Details — do not re-fetch or re-add.
      if (recipeIdsOnList.has(String(listId))) return
      if (!isAuthenticated) {
        if (setShowLoginModal) setShowLoginModal(true)
        else window.dispatchEvent(new CustomEvent('showLoginModal'))
        return
      }
      setAddingFromSavedList(true)
      try {
        const response = await authenticatedFetch(
          `/recipes/${encodeURIComponent(listId)}/pantry-match`,
          { headers: getAuthHeaders() },
        )
        if (!response.ok) {
          console.error('Failed to load pantry match for planned recipe:', response.status)
          setError('Could not check pantry for this recipe. Please try again.')
          return
        }
        const data = await response.json()
        const apiMissing = (data.ingredients ?? []).filter((ing) => !ing?.have)
        if (!apiMissing.length) {
          setError('')
          return
        }
        const recipeName = (listName && String(listName).trim()) || 'Recipe'
        // Shared with Recipe Details: clean names, resolve metadata, then persist.
        // Also drop rows the Shop "In pantry" badge would mark as already covered.
        const pantryNames = await ensurePantryItemNames()
        const { listId: currentListId, items: shoppingItems } =
          await appendRecipeIngredientsToCurrentShoppingList(apiMissing, {
            authenticatedFetch,
            recipeId: listId,
            recipeName,
            storeId: selectedStore?.id,
            pantryItemNames: pantryNames,
          })
        if (!shoppingItems.length) return
        const bindListId = loadedSavedListIsRecipe
          ? null
          : (loadedSavedListIdRef.current || currentListId || null)
        const bindListName = loadedSavedListIsRecipe
          ? null
          : (loadedSavedListName || CURRENT_SHOPPING_LIST_NAME)
        dispatchListAction({
          type: LIST_ACTIONS.APPEND_ITEMS,
          items: shoppingItems,
          listId: bindListId,
          listName: bindListName,
          isRecipe: false,
        })
        void pushCartMutation({
          mode: 'append',
          items: shoppingItems,
          sourceType: 'planned_recipe',
          sourceId: String(listId),
        })
        if (USE_UNIFIED_SHOPPING_VIEW && listSectionRef.current) {
          setTimeout(() => listSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
        }
      } catch (err) {
        console.error('Error adding missing plan recipe items:', err)
        setError('Could not add missing ingredients. Please try again.')
      } finally {
        setAddingFromSavedList(false)
      }
    },
    [
      dispatchListAction,
      ensurePantryItemNames,
      isAuthenticated,
      loadedSavedListIsRecipe,
      loadedSavedListName,
      loadedSavedListIdRef,
      pushCartMutation,
      recipeIdsOnList,
      selectedStore?.id,
      setShowLoginModal,
    ],
  )

  // Listen for loadSavedList events from chat (recipe imports)
  useEffect(() => {
    function handleLoadSavedListEvent(event) {
      const { listId } = event.detail || {}
      appendSavedListItemsById(listId)
    }
    window.addEventListener('loadSavedList', handleLoadSavedListEvent)
    return () => window.removeEventListener('loadSavedList', handleLoadSavedListEvent)
  }, [appendSavedListItemsById])

  // If chat emitted a list id while Shop was not mounted, consume it on mount.
  useEffect(() => {
    try {
      const pendingId = localStorage.getItem('pending_chat_saved_list_id')
      if (pendingId) {
        appendSavedListItemsById(pendingId)
        localStorage.removeItem('pending_chat_saved_list_id')
      }
    } catch {
      // Ignore localStorage errors.
    }
  }, [appendSavedListItemsById])

  useEffect(() => {
    if (!isAuthenticated || !USE_UNIFIED_SHOPPING_VIEW) {
      setRecipeShortcuts([])
      setRecipeShortcutsLoading(false)
      return
    }
    let cancelled = false
    setRecipeShortcutsLoading(true)
    ;(async () => {
      try {
        const res = await authenticatedFetch('/meal-plans/current', { headers: getAuthHeaders() })
        if (!res.ok) {
          if (!cancelled) setRecipeShortcuts([])
          return
        }
        const plan = await res.json()
        if (!cancelled) {
          setRecipeShortcuts(plannedItemsToRecipeShortcuts(plan?.items))
        }
      } catch {
        if (!cancelled) setRecipeShortcuts([])
      } finally {
        if (!cancelled) setRecipeShortcutsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])


  async function searchGroceryItems() {
    if (!input.trim()) return
    
    setLoading(true)
    setError('')
    setResults([])
    setNoMatches([])
    
    // Reset recipe parsing state
    setInputType(null)
    setParsedItems([])
    setDetectionConfidence(null)
    setParsingMetadata(null)
    
    try {
      // Parse input - assume items are separated by commas, newlines, or spaces
      const items = input
        .split(/[,\n]/)
        .map(item => item.trim())
        .filter(item => item.length > 0)
      
      if (items.length === 0) {
        setError('Please enter at least one grocery item')
        return
      }

      // Call the batch lookup API
      const params = new URLSearchParams()
      if (selectedStore?.id) params.set('store', selectedStore.id)
      const response = await fetch(`/grocery/lookup/batch${params.toString() ? `?${params}` : ''}` , {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.detail || errorMessage
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      // Check if response has content before parsing JSON
      const responseText = await response.text()
      if (!responseText.trim()) {
        throw new Error('Server returned empty response')
      }

      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        console.error('JSON parse error:', parseError, 'Response:', responseText)
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`)
      }
      
      const { sortedAisles, failedMatches } = partitionLookupResults(data.results)

      // Store recipe parsing information if available
      if (data.input_type) {
        setInputType(data.input_type)
        setDetectionConfidence(data.detection_confidence)
        setParsingMetadata(data.parsing_metadata)
        
        if (data.parsed_items && data.parsed_items.length > 0) {
          setParsedItems(data.parsed_items)
        }
      }

      // Matched names go on the list immediately. Unmatched names stay visible
      // so they can be corrected without confirming each found item.
      const added = appendMatchedAislesToList(sortedAisles, 'manual_search')
      if (added) {
        setResults([])
        setNoMatches(failedMatches)
        setLookupResultsExpanded(failedMatches.length > 0)
        setInput('')
        scrollToShoppingList()
      } else {
        setResults(sortedAisles)
        setNoMatches(failedMatches)
        setLookupResultsExpanded(true)
      }

    } catch (err) {
      setError(`Error: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      searchGroceryItems()
    }
  }

  function clearResults() {
    setResults([])
    setNoMatches([])
    setError('')
    setInput('')
    
    // Clear recipe parsing state
    setInputType(null)
    setParsedItems([])
    setDetectionConfidence(null)
    setParsingMetadata(null)
  }

  /** If not authenticated, shows login modal and returns false; otherwise returns true. */
  function requireAuthForGroceryAdd() {
    if (isAuthenticated) return true
    if (setShowLoginModal) {
      setShowLoginModal(true)
    } else {
      window.dispatchEvent(new CustomEvent('showLoginModal'))
    }
    return false
  }

  function groceryItemsFromAisles(sortedAisles) {
    const timestamp = Date.now()
    const allItems = []
    sortedAisles.forEach((aisle) => {
      aisle.items.forEach((item) => {
        allItems.push({
          id: `${item.item}-${item.aisle_id}-${timestamp}-${Math.random()}`,
          item: capitalizeFirstGroceryWord(item.item),
          category: item.category,
          aisle_id: item.aisle_id,
          aisle_name: item.aisle_name,
          confidence: item.confidence,
          completed: false,
          completedAt: null,
          addedAt: timestamp
        })
      })
    })
    return allItems
  }

  /** Adds matched lookup rows to the live list. Returns false when there is nothing to add or auth is required. */
  function appendMatchedAislesToList(sortedAisles, sourceType) {
    const allItems = groceryItemsFromAisles(sortedAisles)
    if (allItems.length === 0) return false
    if (!requireAuthForGroceryAdd()) return false
    dispatchListAction({
      type: LIST_ACTIONS.APPEND_ITEMS,
      items: allItems,
      listId: loadedSavedListIdRef.current || null,
      listName: loadedSavedListName,
      isRecipe: loadedSavedListIsRecipe,
    })
    void pushCartMutation({
      mode: 'append',
      items: allItems,
      sourceType,
    })
    if (recipeSaved) setRecipeSaved(false)
    return true
  }

  function addItemToGroceryList(item) {
    if (!appendMatchedAislesToList([{ items: [item] }], 'manual_search')) return
    
    // Remove this item from search results
    setResults(prevResults => {
      return prevResults.map(aisle => ({
        ...aisle,
        items: aisle.items.filter(i => 
          !(i.item === item.item && i.aisle_id === item.aisle_id)
        )
      })).filter(aisle => aisle.items.length > 0) // Remove empty aisles
    })
    
    // Re-enable upload button after recipe is saved and items are added
    if (recipeSaved) {
      setRecipeSaved(false)
    }
  }

  function addAllItemsToGroceryList() {
    if (!appendMatchedAislesToList(results, 'manual_bulk_search')) return
    setResults([])
  }

  const clearGroceryList = useCallback(() => {
    const listIdToEmpty = loadedSavedListId
    // Never PUT [] to a recipe-backed saved list — that would erase the recipe in the DB.
    const shouldPersistEmpty = !!(isAuthenticated && !loadedSavedListIsRecipe)

    if (shouldPersistEmpty) {
      chatHydrationSuppressedRef.current = true
    }
    chatListHydratedIdRef.current = null
    // Prevent cart shadow from racing DELETE and restoring items.
    cartShadowHydrateBackoffUntilRef.current = Date.now() + 2500

    dispatchListAction({ type: LIST_ACTIONS.CLEAR_ALL })
    try {
      localStorage.removeItem('active_cart_id')
      localStorage.removeItem('grocery_list')
      localStorage.removeItem('active_saved_list_id')
      localStorage.removeItem('pending_chat_saved_list_id')
    } catch {
      // ignore
    }
    if (accountAuthenticated && CART_DUAL_WRITE_ENABLED) {
      void authenticatedFetch('/cart/current/items', {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      }).catch((err) => console.warn('[GrocerySearch] Cart clear dual-write failed:', err))
    }

    if (!shouldPersistEmpty) {
      chatHydrationSuppressedRef.current = false
      return
    }

    void (async () => {
      try {
        // Prefer the list currently bound in Shop; otherwise empty Current Shopping List
        // so items added via "+ Add missing" cannot resurrect after clear.
        let idToEmpty = listIdToEmpty
        if (!idToEmpty) {
          try {
            idToEmpty = await resolveCurrentShoppingListId(authenticatedFetch)
          } catch (err) {
            console.warn('[GrocerySearch] Could not resolve Current Shopping List for clear:', err)
          }
        }
        if (!idToEmpty) return
        const response = await authenticatedFetch(`/saved-lists/${idToEmpty}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ items: [] }),
        })
        if (!response.ok) {
          console.warn('[GrocerySearch] Failed to persist empty list after clear:', response.status)
        } else {
          invalidateSavedListsCache()
        }
      } catch (err) {
        console.warn('[GrocerySearch] Failed to persist empty list after clear:', err)
      } finally {
        chatHydrationSuppressedRef.current = false
      }
    })()
  }, [accountAuthenticated, dispatchListAction, isAuthenticated, loadedSavedListId, loadedSavedListIsRecipe])

  /**
   * Keep canonical cart in sync when the user removes items via X.
   * Clear list already DELETEs the cart; X-remove previously only updated local + saved list,
   * so cart shadow / chat hydrate could resurrect deleted lines.
   */
  const syncCanonicalCartFromList = useCallback(
    (items) => {
      const nextItems = Array.isArray(items) ? items : []
      if (nextItems.length === 0) {
        // Suppress auto-hydrate while empty PUT / cart DELETE settle (even if dual-write is off).
        chatHydrationSuppressedRef.current = true
        cartShadowHydrateBackoffUntilRef.current = Date.now() + 2500
        window.setTimeout(() => {
          chatHydrationSuppressedRef.current = false
        }, 800)
      }
      if (!accountAuthenticated || !CART_DUAL_WRITE_ENABLED) return
      if (nextItems.length === 0) {
        void authenticatedFetch('/cart/current/items', {
          method: 'DELETE',
          headers: {
            ...getAuthHeaders(),
          },
        }).catch((err) => console.warn('[GrocerySearch] Cart clear after X-remove failed:', err))
        return
      }
      void pushCartMutation({
        mode: 'replace',
        items: nextItems,
        sourceType: 'manual_remove',
      })
    },
    [accountAuthenticated, pushCartMutation]
  )

  // Clear cart shadow backoff when the user clears the list (length goes from >0 to 0) so we re-hydrate immediately.
  useEffect(() => {
    const prev = prevGroceryListLenForShadowHydrateRef.current
    if (prev > 0 && groceryListItems.length === 0) {
      // Only force immediate rehydrate when not intentionally suppressing (Clear list / X-empty).
      if (!chatHydrationSuppressedRef.current) {
        cartShadowHydrateBackoffUntilRef.current = 0
      }
    }
    prevGroceryListLenForShadowHydrateRef.current = groceryListItems.length
  }, [groceryListItems.length])

  // Shadow/read adapter: hydrate Shop from canonical cart when enabled.
  useEffect(() => {
    if (!CART_READ_ENABLED || !accountAuthenticated) return
    if (activeGroceryTab !== 'shopping') return
    if (groceryListItems.length > 0) return
    if (chatHydrationSuppressedRef.current) return
    if (Date.now() < cartShadowHydrateBackoffUntilRef.current) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await authenticatedFetch('/cart/current', { headers: getAuthHeaders() })
        if (!res.ok || cancelled) return
        const cart = await res.json()
        if (cancelled || !Array.isArray(cart?.items)) return
        // Empty cart: nothing to hydrate (avoids clobbering loadedSavedListId / racing empty PUT).
        if (cart.items.length === 0) return
        if (chatHydrationSuppressedRef.current || cancelled) return
        const cartItems = cart.items.map((item, idx) => {
          const rawId = item.id
          const stableId =
            rawId != null && String(rawId).trim() !== ''
              ? rawId
              : `cart-hydrate-${idx}`
          return {
            id: stableId,
            item: item.item,
            category: item.category ?? null,
            aisle_id: item.aisle_id ?? null,
            aisle_name: item.aisle_name ?? null,
            confidence: typeof item.confidence === 'number' ? item.confidence : null,
            completed: !!item.completed,
            completedAt: item.completedAt ?? null,
            addedAt: item.addedAt ?? null,
          }
        })
        cartShadowHydrateBackoffUntilRef.current = 0
        const dispatch = dispatchListActionRef.current
        if (typeof dispatch === 'function') {
          dispatch({
            type: LIST_ACTIONS.REPLACE_FROM_LIST,
            items: mapGroceryItemsDisplayNames(cartItems),
            listId: null,
            listName: 'Current Shopping Cart',
            isRecipe: false,
            // Empty UI: replace, do not merge — mergeUnique only adds and can resurrect deletes.
            preserveCurrentOnSameList: false,
          })
        }
        try {
          if (cart?.id) localStorage.setItem('active_cart_id', String(cart.id))
        } catch {
          // ignore
        }
        if (typeof cart.parity?.item_count === 'number' && cart.parity.item_count !== cartItems.length) {
          setCartAdapterMismatch(`Cart parity mismatch: expected ${cart.parity.item_count}, rendered ${cartItems.length}`)
        } else {
          setCartAdapterMismatch(null)
        }
      } catch (err) {
        cartShadowHydrateBackoffUntilRef.current = Date.now() + 5000
        console.warn('[GrocerySearch] Cart read adapter fallback to legacy path:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [accountAuthenticated, activeGroceryTab, dispatchListActionRef, groceryListItems.length])

  // eslint-disable-next-line no-unused-vars
  const handleLoadList = useCallback(async (items, listId, listName) => {
    if (!requireAuthForGroceryAdd()) return
    try {
      // Validate items
      if (!Array.isArray(items)) {
        console.error('Invalid items format:', items)
        alert('Error: Invalid list format. Items must be an array.')
        return
      }
      
      // Add items via canonical update path.
      dispatchListAction({
        type: LIST_ACTIONS.APPEND_ITEMS,
        items,
        listId: null,
        listName: null,
        isRecipe: false,
      })
      void pushCartMutation({
        mode: 'append',
        items,
        sourceType: 'manual_list_load',
      })
    } catch (err) {
      console.error('Error in handleLoadList:', err)
      alert(`Error loading list: ${err.message}`)
    }
  // requireAuthForGroceryAdd is stable; omit from deps to avoid callback churn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchListAction, pushCartMutation])

  // Apply pending load from Recipes tab when Shop mounts with initialLoadList; then refresh aisle mappings in background after a delay (avoids flicker when adding multiple recipes)
  useEffect(() => {
    if (!initialLoadList || !Array.isArray(initialLoadList.items)) return
    if (!isAuthenticated) {
      if (setShowLoginModal) setShowLoginModal(true)
      else window.dispatchEvent(new CustomEvent('showLoginModal'))
      onClearPendingLoadList?.()
      return
    }
    const items = initialLoadList.items
    const listId = initialLoadList.id
    const listName = initialLoadList.name ?? null
    const isRecipe = initialLoadList.isRecipe ?? false
    const shoppingItems = isRecipe
      ? stampRecipeSourceOnItems(items, {
          isRecipe: true,
          listId,
          listName: listName || 'Recipe',
        })
      : items
    dispatchListAction({
      type: LIST_ACTIONS.APPEND_ITEMS,
      items: shoppingItems,
      listId: loadedSavedListIsRecipe ? null : loadedSavedListIdRef.current,
      listName: loadedSavedListIsRecipe ? null : loadedSavedListName,
      isRecipe: false,
    })
    void pushCartMutation({
      mode: 'append',
      items: shoppingItems,
      sourceType: isRecipe ? 'recipe' : 'saved_list',
      sourceId: listId ? String(listId) : null,
    })
    // Clear parent's pending list after a short delay so the progress/loader stays visible (min ~700ms)
    const clearDelayMs = 700
    const clearTimeoutId = setTimeout(() => {
      onClearPendingLoadList?.()
    }, clearDelayMs)
    // Schedule aisle refresh after delay so multiple quick adds result in one merge
    if (listId) {
      scheduleDelayedAisleRefresh(listId)
    }
    return () => {
      clearTimeout(clearTimeoutId)
    }
  }, [
    dispatchListAction,
    initialLoadList,
    isAuthenticated,
    loadedSavedListIsRecipe,
    loadedSavedListName,
    loadedSavedListIdRef,
    onClearPendingLoadList,
    pushCartMutation,
    scheduleDelayedAisleRefresh,
    setShowLoginModal,
  ])

  function getConfidenceTier(confidence) {
    if (confidence == null) return 'na'
    if (confidence >= 0.8) return 'high'
    if (confidence >= 0.6) return 'medium'
    return 'low'
  }

  function getConfidenceColor(confidence) {
    if (confidence >= 0.8) return 'var(--color-green-600)'
    if (confidence >= 0.6) return 'var(--color-orange-600)'
    return 'var(--color-error)'
  }

  function getConfidenceText(confidence) {
    if (confidence >= 0.8) return 'High'
    if (confidence >= 0.6) return 'Medium'
    return 'Low'
  }

  function getItemKey(item) {
    // Create a unique key for the item based on item name and aisle_id
    return `${item.item}-${item.aisle_id || ''}`
  }

  function shouldShowSuggestionButton(item) {
    // Don't show if suggestion has already been submitted for this item
    if (submittedSuggestions.has(getItemKey(item))) {
      return false
    }
    // Smart triggering logic - only show for items that need better categorization
    if (!item.category || item.category.toLowerCase() === 'unknown') return true
    if (item.confidence && item.confidence < 0.5) return true
    if (item.method === 'llm_fallback' && item.confidence && item.confidence < 0.7) return true
    if (item.category && item.category.toLowerCase().includes('general')) return true
    return false
  }

  function handleSuggestionClick(item) {
    setSelectedItem(item)
    setShowSuggestionModal(true)
  }

  function handleSuggestionSubmitted(item, suggestedCategory) {
    console.log(`Suggestion submitted: ${item.item} -> ${suggestedCategory}`)
    
    // Mark this item as having a submitted suggestion
    const itemKey = getItemKey(item)
    setSubmittedSuggestions(prev => new Set([...prev, itemKey]))
    
    // Remove item from search results
    setResults(prevResults => {
      return prevResults.map(aisle => ({
        ...aisle,
        items: aisle.items.filter(i => getItemKey(i) !== itemKey)
      })).filter(aisle => aisle.items.length > 0) // Remove empty aisles
    })
    
    // Remove item from no matches list
    setNoMatches(prevNoMatches => 
      prevNoMatches.filter(i => getItemKey(i) !== itemKey)
    )
  }

  function buildRecipeUploadMetadata(data, fallbackName) {
    const source = data && typeof data === 'object' ? data : {}
    const metadata = {
      is_recipe: true,
      recipe_name: fallbackName,
      source: 'recipe_image_upload',
      import_method: 'image',
    }
    const filename = source.filename || source.original_filename
    if (filename) metadata.original_filename = filename
    if (source.extraction_method) metadata.extraction_method = source.extraction_method
    if (typeof source.confidence === 'number') metadata.parsing_confidence = source.confidence

    const metadataKeys = ['author', 'cuisine', 'prep_time', 'cook_time', 'total_time', 'servings']
    metadataKeys.forEach((key) => {
      const value = source[key]
      if (value != null && String(value).trim()) metadata[key] = String(value).trim()
    })

    const description = source.description && String(source.description).trim()
    if (description) metadata.summary = description

    if (Array.isArray(source.instructions)) {
      const instructions = source.instructions
        .map((step) => String(step || '').trim())
        .filter(Boolean)
      if (instructions.length > 0) metadata.instructions = instructions
    }

    return metadata
  }

  async function saveRecipeFromImageUpload(confirmedName, uploadData, sortedAisles) {
    const recipeLabel = String(confirmedName || recipeName || '').trim()
    if (!recipeLabel) return

    if (!isAuthenticated) {
      if (setShowLoginModal) {
        setShowLoginModal(true)
      } else {
        window.dispatchEvent(new CustomEvent('showLoginModal'))
      }
      return
    }

    setSavingRecipe(true)
    setError('')
    try {
      let itemsPayload = []
      if (sortedAisles.length > 0) {
        const now = Date.now()
        sortedAisles.forEach((aisle) => {
          aisle.items.forEach((r) => {
            itemsPayload.push({
              id: `${r.item}-${r.aisle_id}-${now}-${Math.random()}`,
              item: r.item,
              category: r.category || null,
              aisle_id: r.aisle_id || null,
              aisle_name: r.aisle_name || null,
              confidence: typeof r.confidence === 'number' ? r.confidence : null,
              completed: false,
              completedAt: null,
              addedAt: now,
            })
          })
        })
      } else {
        const now = Date.now()
        itemsPayload = (Array.isArray(uploadData?.items) ? uploadData.items : imageParsedItems).map((name) => ({
          id: `${name}-${now}-${Math.random()}`,
          item: name,
          category: null,
          aisle_id: null,
          aisle_name: null,
          confidence: null,
          completed: false,
          completedAt: null,
          addedAt: now,
        }))
      }

      const data = uploadData || pendingUploadData || {}
      const extractedDescription = (data.description && String(data.description).trim()) ? String(data.description).trim() : null
      const descriptionFromUpload = extractedDescription || 'Saved from recipe image upload'
      const existingSavedListId = data.saved_list_id || data.list_id || null
      const existingSavedListName = data.saved_list_name || data.name || null
      const metadataFromUpload = buildRecipeUploadMetadata(data, recipeLabel)
      let savedRecipe = null

      if (existingSavedListId) {
        const updateBody = { name: recipeLabel }
        if (extractedDescription) updateBody.description = extractedDescription
        if (recipeAuthor.trim()) updateBody.author = recipeAuthor.trim()
        const resp = await fetch(`/saved-lists/${existingSavedListId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updateBody),
        })
        if (!resp.ok) {
          if (resp.status === 401 || resp.status === 403) {
            if (setShowLoginModal) setShowLoginModal(true)
            else window.dispatchEvent(new CustomEvent('showLoginModal'))
            return
          }
          const txt = await resp.text()
          throw new Error(txt || `HTTP ${resp.status}`)
        }
        savedRecipe = await resp.json().catch(() => ({
          id: existingSavedListId,
          name: existingSavedListName || recipeLabel,
        }))
      } else {
        const body = {
          name: recipeLabel,
          description: descriptionFromUpload,
          items: itemsPayload,
          tags: ['recipe'],
          is_favorite: false,
          author: recipeAuthor.trim() || metadataFromUpload.author || null,
          list_metadata: metadataFromUpload,
        }
        const resp = await fetch('/saved-lists/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(body),
        })
        if (!resp.ok) {
          if (resp.status === 401 || resp.status === 403) {
            if (setShowLoginModal) setShowLoginModal(true)
            else window.dispatchEvent(new CustomEvent('showLoginModal'))
            return
          }
          const txt = await resp.text()
          throw new Error(txt || `HTTP ${resp.status}`)
        }
        savedRecipe = await resp.json().catch(() => null)
      }

      const savedRecipeId = savedRecipe?.id ?? savedRecipe?.list_id ?? null
      const savedRecipeName = String(savedRecipe?.name || recipeLabel || 'Recipe').trim()
      setRecipeSaved(true)
      setRecipeAuthor('')
      setImageParsedItems([])
      setPendingUploadData(null)
      window.dispatchEvent(new CustomEvent('recipeImported', {
        detail: {
          listId: savedRecipeId,
          name: savedRecipeName,
          source: 'image',
          scroll: false,
        },
      }))
      onRecipeImageImportFlowComplete?.()
    } catch (err) {
      setError(`Failed to save recipe: ${err.message}`)
    } finally {
      setSavingRecipe(false)
    }
  }

  function scrollToShoppingList() {
    setTimeout(() => {
      const target = USE_UNIFIED_SHOPPING_VIEW ? listSectionRef.current : document.getElementById('grocery-list-panel')
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  async function handleRenameConfirm(confirmedName) {
    const recipeLabel = String(confirmedName || '').trim()
    if (!recipeLabel) return

    if (!isAuthenticated) {
      if (setShowLoginModal) setShowLoginModal(true)
      else window.dispatchEvent(new CustomEvent('showLoginModal'))
      return
    }

    setRecipeName(recipeLabel)

    if (!pendingUploadData) {
      setShowRenameModal(false)
      return
    }
    const data = pendingUploadData

    let sortedAisles = []
    let failedMatches = []
    if (Array.isArray(data.results)) {
      const partitioned = partitionLookupResults(
        data.results.map((r) => ({ ...r, item: r.item || r.name }))
      )
      sortedAisles = partitioned.sortedAisles
      failedMatches = partitioned.failedMatches
    }

    const parsedNames = Array.isArray(data.items) ? data.items : imageParsedItems
    const groceryItems = buildGroceryItemsFromLookup({
      sortedAisles,
      failedMatches,
      parsedNames,
    })

    if (groceryItems.length > 0) {
      dispatchListAction({
        type: LIST_ACTIONS.APPEND_ITEMS,
        items: groceryItems,
        listId: loadedSavedListIdRef.current || null,
        listName: loadedSavedListName,
        isRecipe: loadedSavedListIsRecipe,
      })
      void pushCartMutation({
        mode: 'append',
        items: groceryItems,
        sourceType: 'recipe_image_upload',
      })
    }

    await saveRecipeFromImageUpload(recipeLabel, data, sortedAisles)

    setShowRenameModal(false)
    setRecipeImportNotice({
      name: recipeLabel,
      addedCount: groceryItems.length,
      unmatchedCount: failedMatches.length,
    })
    scrollToShoppingList()
  }

  function handleRenameCancel() {
    setShowRenameModal(false)
    setPendingUploadData(null)
    onRecipeImageImportFlowComplete?.()
    // Reset state
    setImageParsedItems([])
    setImageOcrPreview('')
    setRecipeName('')
    setRecipeAuthor('')
  }

  // When user selected/took a photo in Add Recipe modal (Recipes tab), run upload and recipe import
  useEffect(() => {
    if (!pendingRecipeImageFile) return
    const file = pendingRecipeImageFile
    if (processedPendingRecipeImageFileRef.current === file) return
    processedPendingRecipeImageFileRef.current = file
    _uploadAndParseRecipeImage(file)
    // Intentionally omit _uploadAndParseRecipeImage from deps: we only want to run when file is set, not when the function ref changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRecipeImageFile])

  const shopPageBannerItems = useMemo(() => {
    const items = []
    if (initialLoadList != null || pendingQueueLength > 0) {
      items.push({
        id: 'shop-add-progress',
        variant: 'info',
        node: (
          <StateBanner
            variant="info"
            size="inline"
            className="shop-notice shop-notice--progress"
            role="status"
            aria-live="polite"
            aria-label={initialLoadList ? `Adding ${initialLoadList.name ?? 'list'} to grocery list` : 'Adding to list'}
            headline={initialLoadList ? `Adding "${initialLoadList.name ?? 'list'}" to list` : 'Adding to list'}
            body={
              <>
                {pendingQueueLength > 0 ? (
                  <span>{pendingQueueLength} more in queue</span>
                ) : (
                  <span>Updating your shopping list…</span>
                )}
                <div className="shop-add-progress-track" aria-hidden="true">
                  <div className="shop-add-progress-bar" />
                </div>
              </>
            }
          />
        ),
      })
    }
    if (shopRefreshError) {
      items.push({
        id: 'shop-refresh-error',
        variant: 'urgent',
        node: (
          <StateBanner
            variant="urgent"
            size="inline"
            className="shop-notice"
            role="alert"
            headline="Couldn’t refresh list"
            body={shopRefreshError}
            action={{ label: 'Retry', onClick: retryShopRefresh }}
            dismissible
            onDismiss={clearShopRefreshError}
          />
        ),
      })
    }
    if (chatHydrationNotice) {
      items.push({
        id: 'shop-chat-hydration',
        variant: 'info',
        node: (
          <StateBanner
            variant="info"
            size="inline"
            className="shop-notice"
            role="status"
            body={chatHydrationNotice}
          />
        ),
      })
    }
    if (cartAdapterMismatch) {
      items.push({
        id: 'shop-cart-mismatch',
        variant: 'soft',
        node: (
          <StateBanner
            variant="soft"
            size="inline"
            className="shop-notice"
            role="status"
            body={cartAdapterMismatch}
          />
        ),
      })
    }
    if (showShopIntro) {
      items.push({
        id: 'shop-intro',
        variant: 'soft',
        node: (
          <DismissibleOnboardingMessage
            messageId="shopIntro"
            className="onboarding-message-left"
            isVisible={showShopIntro}
            onDismiss={dismissShopIntro}
          />
        ),
      })
    }
    return items
  }, [
    initialLoadList,
    pendingQueueLength,
    shopRefreshError,
    retryShopRefresh,
    clearShopRefreshError,
    chatHydrationNotice,
    cartAdapterMismatch,
    showShopIntro,
    dismissShopIntro,
  ])

  return (
    <>
      {/* Non-blocking recipe upload status: top progress bar so user can keep using the app (portaled so visible from any tab) */}
      {(_uploadingImage || savingRecipe) && createPortal(
        <div
          className="recipe-upload-progress-bar"
          role="status"
          aria-live="polite"
          aria-label={savingRecipe ? 'Saving recipe' : 'Importing recipe from image'}
        >
          <div className="recipe-upload-progress-fill" aria-hidden="true" />
        </div>,
        document.body
      )}
      {(_uploadingImage || savingRecipe) && createPortal(
        <div
          className="recipe-upload-toast"
          role="status"
          aria-live="polite"
          aria-label={savingRecipe ? 'Saving recipe' : 'Importing recipe from image'}
        >
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
          <span className="shop-import-status-primary">
            {savingRecipe ? 'Saving recipe…' : 'Importing recipe…'}
          </span>
          <span className="shop-import-status-secondary">
            {savingRecipe ? 'Adding it to your recipes' : 'Uploading and reading the image'}
          </span>
        </div>,
        document.body
      )}
    <div className="tab-content-wrapper">
      <div className="shop-content-inner">
        {/* Unified Shopping Panel (Option B: list-centric with embedded lookup strip) */}
        {USE_UNIFIED_SHOPPING_VIEW && activeGroceryTab === 'shopping' && (
          <div id="shopping-panel" className="shop-tab-layout" role="tabpanel" aria-labelledby="shopping-tab">
            {/* Shop v2 list panel — cream canvas; header uses .eyebrow. "Add items" stays body type. */}
            <div
              ref={listSectionRef}
              id="grocery-list-panel"
              className="shop-list-panel shop-list-panel--v2"
              role="region"
              aria-label="Shopping list panel"
            >
              <div className="shop-list-panel-v2-body">
                <GroceryList
                  items={groceryListItems}
                  onDispatchListAction={dispatchListAction}
                  onClearAll={clearGroceryList}
                  onSyncCanonicalCart={syncCanonicalCartFromList}
                  loadedSavedListId={loadedSavedListId}
                  loadedSavedListName={loadedSavedListName}
                  loadedSavedListIsRecipe={loadedSavedListIsRecipe}
                  loadedSavedListOriginalItemNames={loadedSavedListOriginalItemNames}
                  selectedStore={selectedStore}
                  pantryItemNames={pantryItemNames}
                  storeHeaderSlot={<StoreSelector variant="listHeader" />}
                  onClearLoadedList={() => { setLoadedSavedListId(null); setLoadedSavedListName(null); setLoadedSavedListIsRecipe(false); setLoadedSavedListOriginalItemNames(null) }}
                  pageNotices={
                    <StateBannerStack
                      className="shop-notice-stack"
                      maxVisible={1}
                      items={shopPageBannerItems}
                    />
                  }
                />
              </div>
            </div>
            {/* Add items + lookup results — v2 cream panel, aligned to list column on desktop */}
            <div className="shop-below-list">
            <section className="shop-add-panel shop-add-panel--v2" aria-labelledby="shop-add-heading">
              <header className="shop-add-panel-header">
                <h2 id="shop-add-heading" className="shop-add-panel-title">Add items</h2>
              </header>
              <div className="shop-add-panel-body">
                <div className="shop-add-textarea-wrap">
                  <label htmlFor="grocery-input-unified" className="sr-only">Enter grocery items</label>
                  <TextArea
                    id="grocery-input-unified"
                    className="shop-add-textarea"
                    name="shopping-list-add-items"
                    inputMode="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    enterKeyHint="search"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="milk, eggs, butter…"
                  />
                </div>
                <Button
                  id="grocery-search-button"
                  type="button"
                  onClick={searchGroceryItems}
                  disabled={loading || !input.trim()}
                  className={loading || !input.trim() ? 'secondary shop-add-action-btn' : 'shop-add-action-btn primary'}
                  aria-label={loading ? 'Searching for grocery items' : 'Search or parse and add grocery items'}
                >
                  {loading ? <><i className="fas fa-spinner fa-spin icon-sm icon-inverse" aria-hidden="true" /> Searching…</> : <><i className="fas fa-plus icon-sm icon-inverse" aria-hidden="true" /> Add to list</>}
                </Button>
                {(results.length > 0 || noMatches.length > 0) && (
                  <Button
                    id="grocery-clear-results-button"
                    type="button"
                    onClick={clearResults}
                    className="link-cta link-cta-standalone shop-clear-results-btn"
                    aria-label="Clear search results"
                  >
                    <i className="fas fa-trash icon-sm icon-muted" aria-hidden="true" /> Clear search results
                  </Button>
                )}
              {/* Recipe detection - only for recipe input (Item List Detected section removed) */}
              {inputType === 'recipe' && (
                <div className="shop-recipe-detected-card">
                  <div className="shop-recipe-detected-title">
                    <i className="fas fa-utensils icon-sm icon-accent" aria-hidden="true" />
                    <span>Recipe detected</span>
                    {detectionConfidence != null && (
                      <span className="shop-recipe-detected-confidence">
                        {Math.round(detectionConfidence * 100)}%
                      </span>
                    )}
                  </div>
                  {parsedItems.length > 0 && (
                    <div className="shop-recipe-detected-items">
                      <div className="shop-recipe-detected-items-label">
                        Extracted ingredients ({parsedItems.length})
                      </div>
                      <div className="shop-recipe-detected-chips">
                        {parsedItems.map((item, index) => (
                          <span key={index} className="badge badge-category">{item}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {imageParsedItems.length > 0 && !recipeSaved && (
                <StateBanner
                  variant="info"
                  size="inline"
                  headline="Recipe imported"
                  body={`${imageParsedItems.length} ingredients parsed — confirm the recipe name to save and add them to your list.`}
                  className="shop-recipe-import-hint"
                />
              )}
              {recipeImportNotice && (
                <StateBanner
                  variant="soft"
                  size="inline"
                  dismissible
                  onDismiss={() => setRecipeImportNotice(null)}
                  headline={`${recipeImportNotice.name} saved`}
                  body={
                    recipeImportNotice.addedCount > 0
                      ? `${recipeImportNotice.addedCount} ingredient${recipeImportNotice.addedCount === 1 ? '' : 's'} added to your shopping list.${
                          recipeImportNotice.unmatchedCount > 0
                            ? ` ${recipeImportNotice.unmatchedCount} could not be matched to a store aisle.`
                            : ''
                        }`
                      : 'Recipe saved to your collection.'
                  }
                  className="shop-recipe-import-notice"
                />
              )}
              {error && (
                <StateBanner
                  variant="urgent"
                  size="inline"
                  className="shop-inline-error"
                  role="alert"
                  body={error}
                  dismissible
                  onDismiss={() => setError('')}
                />
              )}
              {accountAuthenticated && (
                <>
                  <div className="shop-section-divider" aria-hidden="true">
                    <span className="shop-section-divider-line" />
                    <span className="shop-section-divider-label">or add from your plan</span>
                    <span className="shop-section-divider-line" />
                  </div>
                  <section className="shop-from-recipe" aria-label="Add from planned recipes">
                    {recipeShortcutsLoading && (
                      <p className="page-subtitle shop-inline-subtitle">Loading recipes…</p>
                    )}
                    {!recipeShortcutsLoading && recipeShortcuts.length > 0 && (
                      <div className="recipe-shortcut-list">
                        {recipeShortcuts.map((list, idx) => {
                          const dotClass = ['recipe-shortcut-dot--a', 'recipe-shortcut-dot--b', 'recipe-shortcut-dot--c'][idx % 3]
                          const count = list.item_count ?? list.items?.length ?? 0
                          const missingCount = list.missing_count
                          const inPantry = missingCount === 0
                          const alreadyOnList = recipeIdsOnList.has(String(list.id))
                          const countLabel =
                            missingCount != null
                              ? inPantry
                                ? 'all in pantry'
                                : alreadyOnList
                                  ? null
                                  : `${missingCount} to shop`
                              : count > 0
                                ? `${count} ingredients`
                                : null
                          const buttonLabel = inPantry
                            ? 'In pantry'
                            : alreadyOnList
                              ? 'On list'
                              : '+ Add missing'
                          const ariaLabel = inPantry
                            ? `${list.name} — all ingredients already in pantry`
                            : alreadyOnList
                              ? `${list.name} — ingredients already on shopping list`
                              : `Add missing ingredients from ${list.name}`
                          return (
                            <div key={list.planItemId || list.id} className="recipe-shortcut">
                              <span className={`recipe-shortcut-dot ${dotClass}`} aria-hidden="true" />
                              <span className="recipe-shortcut-name">{list.name}</span>
                              <span className="recipe-shortcut-meta">
                                {[list.plannedDateLabel, countLabel].filter(Boolean).join(' • ')}
                              </span>
                              <Button
                                type="button"
                                className="secondary recipe-shortcut-add-all"
                                disabled={addingFromSavedList || inPantry || alreadyOnList}
                                onClick={() => appendMissingPlanRecipeItemsById(list.id, list.name)}
                                aria-label={ariaLabel}
                              >
                                {buttonLabel}
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {!recipeShortcutsLoading && recipeShortcuts.length === 0 && (
                      <p className="page-subtitle shop-inline-subtitle">
                        Add dated recipes to your current plan to show them here.
                      </p>
                    )}
                  </section>
                </>
              )}
              </div>
            </section>

            {/* Found items - expandable */}
            {(results.length > 0 || noMatches.length > 0) && (
              <div className={`shop-found-panel shop-found-panel--v2 ${lookupResultsExpanded ? 'shop-found-panel--open' : ''}`}>
                {results.length > 0 && (
                <div className="shop-found-panel-top">
                  <button
                    type="button"
                    className="shop-found-panel-header"
                    onClick={() => setLookupResultsExpanded(prev => !prev)}
                    aria-expanded={lookupResultsExpanded}
                    aria-controls="unified-found-body"
                  >
                    <span>
                      <i className="fas fa-store icon-base icon-muted" aria-hidden="true"></i>
                      Found {results.reduce((sum, aisle) => sum + aisle.items.length, 0)} items
                      {noMatches.length > 0 && ` · ${noMatches.length} not found`}
                    </span>
                    <i className={`fas fa-chevron-down ${lookupResultsExpanded ? 'unified-chevron-open' : ''}`} aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => addAllItemsToGroceryList()}
                    className="primary shop-unified-found-add-all"
                    aria-label="Add all found items to grocery list"
                  >
                    <i className="fas fa-plus icon-sm icon-inverse" aria-hidden="true"></i> Add all
                  </button>
                </div>
                )}
                <div id="unified-found-body" className="shop-unified-found-body" hidden={!lookupResultsExpanded}>
                  {results.map((aisle, index) => (
                    <div key={index} className="shop-aisle-result-card">
                      <div className="shop-aisle-result-heading">
                        <i className="fas fa-store icon-base icon-muted" aria-hidden="true"></i>
                        <span>{aisle.aisle_name}</span>
                      </div>
                      <div className="shop-found-items-wrap">
                        {aisle.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="shop-found-item-chip">
                            <span className="shop-found-item-name">{item.item}</span>
                            {item.category && <span className="badge badge-category shop-found-item-category">{item.category}</span>}
                            <span className="badge shop-found-item-confidence" data-confidence={getConfidenceTier(item.confidence)}>{getConfidenceText(item.confidence)}</span>
                            {shouldShowSuggestionButton(item) && (
                              <button onClick={() => handleSuggestionClick(item)} className="ghost shop-found-item-suggest-btn" title="Suggest better category" aria-label={`Suggest better category for ${item.item}`}>
                                <i className="fas fa-lightbulb icon-sm icon-accent" aria-hidden="true"></i>
                              </button>
                            )}
                            <button onClick={() => addItemToGroceryList(item)} className="primary shop-found-item-add-btn" title="Add to grocery list" aria-label={`Add ${item.item} to grocery list`}>
                              <i className="fas fa-plus icon-sm icon-inverse" aria-hidden="true"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {noMatches.length > 0 && (
                    <div className="shop-no-matches-card">
                      <div className="card-title shop-no-matches-title">
                        <i className="fas fa-exclamation-triangle icon-sm icon-warning" aria-hidden="true"></i>
                        <span>Items not found ({noMatches.length})</span>
                      </div>
                      <div className="shop-no-matches-chips">
                        {noMatches.map((item, index) => (
                          <div key={index} className="shop-no-match-chip">{item.item}</div>
                        ))}
                      </div>
                      <p className="shop-no-matches-hint">Try different names, check spelling, or use more specific terms.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {addingFromSavedList && (
              <div className="shop-adding-recipe-status shop-adding-recipe-status--v2" role="status" aria-live="polite">
                <span className="shop-adding-recipe-row">
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  Adding recipe to list…
                </span>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Tab Content - Legacy 3-tab layout */}
        {!USE_UNIFIED_SHOPPING_VIEW && activeGroceryTab === 'lookup' && (
          <div id="lookup-panel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }} role="tabpanel" aria-labelledby="lookup-tab">
            {/* Search Input Card */}
            <div className="card" style={{ 
              display: 'flex', 
              flexDirection: 'column'
            }}>
              <div className="card-title" style={{ 
                flexShrink: 0, 
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span>Grocery Item Lookup</span>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label htmlFor="grocery-input" className="sr-only">Enter grocery items</label>
                <textarea
                  id="grocery-input"
                  className="input"
                  name="grocery-lookup-input"
                  inputMode="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  enterKeyHint="search"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Enter items like: coffee, milk, bread&#10;or paste a recipe:&#10;Chicken Parmesan Recipe: 2 lbs boneless chicken breast, 1 cup breadcrumbs..."
                  style={{ 
                    fontSize: 'var(--text-xs)', 
                    lineHeight: '1.4', 
                    resize: 'none',
                    width: '100%',
                    minHeight: '200px', // Fallback minimum height
                    maxHeight: 'calc(100dvh - 300px)', // Use dynamic viewport height for address bar minimization
                    height: 'calc(100dvh - 400px)', // Use dynamic viewport height for address bar minimization
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button
                    id="grocery-search-button"
                    onClick={searchGroceryItems}
                    disabled={loading || !input.trim()}
                    className={loading || !input.trim() ? '' : 'primary'}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      fontSize: '14px',
                      padding: '8px 16px'
                    }}
                    aria-label={loading ? 'Searching for grocery items' : 'Search for grocery items'}
                  >
                    {loading ? <><i className="fas fa-spinner fa-spin icon-sm icon-inverse" aria-hidden="true"></i> Searching...</> : <><i className="fas fa-search icon-sm icon-inverse" aria-hidden="true"></i> Search</>}
                  </button>
                  {(results.length > 0 || noMatches.length > 0) && (
                    <button
                      id="grocery-clear-results-button"
                      onClick={clearResults}
                      className="link-cta link-cta-standalone"
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        fontSize: '14px',
                        padding: '8px 16px'
                      }}
                      aria-label="Clear search results"
                    >
                      <i className="fas fa-trash icon-sm icon-muted" aria-hidden="true"></i> Clear
                    </button>
                  )}
                </div>
                {/* Recipe image import completes via rename modal — no duplicate save UI here */}
              </div>

              {error && (
                <div className="error" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span>{error}</span>
                  <button
                    onClick={() => setError('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      fontSize: '18px',
                      lineHeight: '1',
                      opacity: 0.7,
                      transition: 'opacity 0.2s',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                    aria-label="Close error message"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* Recipe Parsing Information - only for recipe input */}
            {inputType === 'recipe' && (
              <div className="card" style={{ 
                marginBottom: '12px',
                background: 'var(--color-orange-50)',
                border: '1px solid var(--color-orange-200)'
              }}>
                <div className="card-title" style={{ 
                  marginBottom: '12px',
                  color: 'var(--color-orange-700)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <><i className="fas fa-utensils icon-sm icon-accent" aria-hidden="true"></i> <span>Recipe Detected</span></>
                  {detectionConfidence && (
                    <span className="badge badge-count" style={{ 
                      fontSize: 'var(--text-xs)',
                      background: 'var(--color-orange-600)'
                    }}>
                      {Math.round(detectionConfidence * 100)}%
                    </span>
                  )}
                </div>
                
                {parsedItems.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div className="shop-recipe-parse-heading">
                      Extracted Ingredients ({parsedItems.length}):
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '4px',
                      fontSize: 'var(--text-xs)'
                    }}>
                      {parsedItems.map((item, index) => (
                        <span key={index} className="badge badge-category">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="shop-recipe-parse-note">
                  Recipe was automatically parsed to extract grocery ingredients. Units of measure and cooking instructions were filtered out.
                </div>
              </div>
            )}

            {/* Search Results - hide when modal is shown */}
            {results.length > 0 && (
              <div className="card" style={{ marginBottom: '12px' }}>
                <div className="card-title" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span>📍 Found Items by Aisle ({results.reduce((sum, aisle) => sum + aisle.items.length, 0)} items)</span>
                  <button 
                    onClick={addAllItemsToGroceryList}
                    className="primary"
                    style={{ 
                      fontSize: '14px',
                      padding: '8px 16px',
                      minHeight: '44px'
                    }}
                    aria-label="Add all found items to grocery list"
                  >
                    <i className="fas fa-plus icon-sm icon-inverse" aria-hidden="true"></i> Add All
                  </button>
                </div>
                {results.map((aisle, index) => (
                <div key={index} className="card" style={{ 
                  marginBottom: '12px',
                  padding: '16px'
                }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    marginBottom: '12px',
                    color: 'var(--color-green-600)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <i className="fas fa-store icon-base icon-muted" aria-hidden="true"></i>
                    <span>{aisle.aisle_name}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {aisle.items.map((item, itemIndex) => (
                      <div key={itemIndex} style={{
                        padding: '8px 12px',
                        background: 'var(--surface-default)',
                        border: '1px solid var(--color-green-100)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minHeight: '44px'
                      }}>
                        <span style={{ fontWeight: '500' }}>{item.item}</span>
                        {item.category && (
                          <span className="badge badge-category" style={{ fontSize: 'var(--text-xs)' }}>
                            {item.category}
                          </span>
                        )}
                        <span className="badge" style={{ 
                          fontSize: 'var(--text-xs)',
                          background: 'transparent',
                          color: getConfidenceColor(item.confidence),
                          borderColor: getConfidenceColor(item.confidence)
                        }}>
                          {getConfidenceText(item.confidence)}
                        </span>
                        {shouldShowSuggestionButton(item) && (
                          <button
                            onClick={() => handleSuggestionClick(item)}
                            className="ghost"
                            style={{
                              padding: '4px',
                              minWidth: 'auto',
                              minHeight: 'auto',
                              width: '32px',
                              height: '32px',
                              color: 'var(--color-orange-600)',
                              fontSize: '16px'
                            }}
                            title="Suggest better category"
                            aria-label={`Suggest better category for ${item.item}`}
                          >
                            <i className="fas fa-lightbulb icon-sm icon-accent" aria-hidden="true"></i>
                          </button>
                        )}
                        <button
                          onClick={() => addItemToGroceryList(item)}
                          className="primary"
                          style={{
                            padding: '4px 8px',
                            fontSize: 'var(--text-xs)',
                            minHeight: 'auto',
                            minWidth: 'auto',
                            width: 'auto'
                          }}
                          title="Add to grocery list"
                          aria-label={`Add ${item.item} to grocery list`}
                        >
                          <i className="fas fa-plus icon-sm icon-inverse" aria-hidden="true"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                ))}
              </div>
            )}

            {/* No Matches - hide when modal is shown */}
            {noMatches.length > 0 && (
              <div className="card" style={{ 
                marginBottom: '12px',
                background: 'var(--surface-default)',
                border: '1px solid var(--red)',
                borderWidth: '2px'
              }}>
                <div className="card-title" style={{ 
                  marginBottom: '12px',
                  color: 'var(--red)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-exclamation-triangle icon-sm icon-warning" aria-hidden="true"></i> 
                  <span>Items Not Found ({noMatches.length})</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {noMatches.map((item, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      background: 'var(--surface-default)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minHeight: '44px'
                    }}>
                      <span>{item.item}</span>
                      {item.confidence < 0.6 && item.confidence > 0 && (
                        <span className="badge" style={{ 
                          background: 'var(--color-orange-50)',
                          borderColor: 'var(--color-orange-200)',
                          color: 'var(--color-orange-700)'
                        }}>
                          Low confidence ({Math.round(item.confidence * 100)}%)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: 'var(--text-secondary)', 
                  lineHeight: '1.5'
                }}>
                  These items couldn't be found or have low confidence matches. Try different names, check spelling, or use more specific terms.
                </div>
              </div>
            )}
          </div>
        )}

        {activeGroceryTab === 'grocery-list' && (
          <div id="grocery-list-panel" className="card" style={{ 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 'calc(100dvh - 280px)' // Use dynamic viewport height for address bar minimization
          }} role="tabpanel" aria-labelledby="grocery-list-tab">
            {/* Add-to-list progress (queue from Recipes/Saved Lists) — same as unified view so loader is always visible */}
            {(initialLoadList != null || pendingQueueLength > 0) && (
              <div
                role="status"
                aria-live="polite"
                aria-label={initialLoadList ? `Adding ${initialLoadList.name ?? 'list'} to grocery list` : 'Adding to list'}
                style={{
                  flexShrink: 0,
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-accent-subtle, rgba(142, 200, 100, 0.15))',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-accent-muted, rgba(142, 200, 100, 0.35))',
                  marginBottom: '8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                  <i className="fas fa-spinner fa-spin" style={{ color: 'var(--color-accent)', fontSize: 'var(--text-sm)' }} aria-hidden="true" />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: '500' }}>
                    {initialLoadList ? `Adding "${initialLoadList.name ?? 'list'}" to list` : 'Adding to list'}
                    {pendingQueueLength > 0 && (
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '400' }}> ({pendingQueueLength} more in queue)</span>
                    )}
                  </span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border, rgba(0,0,0,0.08))', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: '40%',
                      borderRadius: 2,
                      background: 'var(--color-accent)',
                      animation: 'add-to-list-progress 1.2s ease-in-out infinite'
                    }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            )}
            {addingFromSavedList && (
              <div style={{ flexShrink: 0, padding: 'var(--space-3)', background: 'var(--color-accent-subtle, rgba(142, 200, 100, 0.15))', borderRadius: 'var(--radius-md)', marginBottom: '8px' }} role="status" aria-live="polite">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: 'var(--text-sm)' }}>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  Adding recipe to list…
                </span>
              </div>
            )}
            <div className="card-title" style={{ flexShrink: 0, textAlign: 'left' }}>
              Grocery List
            </div>
            
            <div className="mobile-scroll-normalize" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <GroceryList 
                items={groceryListItems}
                onDispatchListAction={dispatchListAction}
                onClearAll={clearGroceryList}
                onSyncCanonicalCart={syncCanonicalCartFromList}
                loadedSavedListId={loadedSavedListId}
                loadedSavedListName={loadedSavedListName}
                loadedSavedListIsRecipe={loadedSavedListIsRecipe}
                loadedSavedListOriginalItemNames={loadedSavedListOriginalItemNames}
                selectedStore={selectedStore}
                pantryItemNames={pantryItemNames}
                onClearLoadedList={() => {
                  setLoadedSavedListId(null)
                  setLoadedSavedListName(null)
                  setLoadedSavedListIsRecipe(false)
                  setLoadedSavedListOriginalItemNames(null)
                }}
              />
            </div>
          </div>
        )}

      </div>

      {/* Category Suggestion Modal - portaled to body so it overlays full viewport */}
      {createPortal(
        <CategorySuggestionModal
          isOpen={showSuggestionModal}
          onClose={() => setShowSuggestionModal(false)}
          item={selectedItem}
          onSuggestionSubmitted={handleSuggestionSubmitted}
          skipAutoCheck={true}
        />,
        document.body
      )}
      {createPortal(
        <UploadErrorModal
          isOpen={showUploadErrorModal}
          onClose={handleCloseUploadErrorModal}
          message={uploadErrorMessage}
        />,
        document.body
      )}
      {createPortal(
        <RecipeRenameModal
          isOpen={showRenameModal}
          onClose={handleRenameCancel}
          initialName={recipeName}
          onConfirm={handleRenameConfirm}
          saving={savingRecipe}
        />,
        document.body
      )}
    </div>
    </>
  )
}

export default GrocerySearch
